import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { FaTimes, FaWallet, FaExclamationCircle, FaCreditCard } from "react-icons/fa";
import { WalletApiService } from "../../../../../service/seller/walletApiService";
import { useLoader } from "../../../../../base/context/LoaderProvider";
import {
  ToastTypes,
  useMessage,
} from "../../../../../base/context/MessageProvider";
import Logo from "../../../../../assets/images/common/logo.png";
import './style.css'

const customStyles = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(4px)",
    zIndex: 10000,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    background: "transparent",
    padding: 0,
    borderRadius: "20px",
    maxWidth: "500px",
    width: "100%",
    maxHeight: "90vh",
    overflow: "visible",
  },
};

Modal.setAppElement("#root");

export default function PaymentPopup({
  modalIsOpen,
  setIsOpen,
  handleRefresh,
}) {
  const [data, setData] = useState("");
  const [error, setError] = useState("");
  const [orderDetails, setOrderDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loader = useLoader();
  const message = useMessage();

  function closeModal() {
    if (!isSubmitting) {
      setIsOpen(false);
      setData("");
      setError("");
    }
  }

  useEffect(() => {
    if (!modalIsOpen) {
      setData("");
      setError("");
      setIsSubmitting(false);
    }
  }, [modalIsOpen]);

  const handleOnchange = (e) => {
    setData(e.target.value);
    setError("");
  };

  const validate = () => {
    let result = true;
    if (!data.length) {
      setError("Amount is required");
      result = false;
    } else if (data < 5000) {
      setError("Minimum Amount 5000");
      result = false;
    }
    return result;
  };

  const handleProceedToPay = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    const rpScript = "https://checkout.razorpay.com/v1/checkout.js";
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = rpScript;
      script.onload = () => {
        openRazorpay();
        resolve(true);
      };
      script.onerror = () => {
        setIsSubmitting(false);
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const openRazorpay = async () => {
    loader.showLoading(true);
    const amountPayload = {
      amount: data * 100,
    };
    await WalletApiService.createOrder(JSON.stringify(amountPayload))
      .then(async (res) => {
        loader.showLoading(false);
        setIsSubmitting(false);
        if (res.status.code === 200) {
          setOrderDetails(res.data);
          closeModal();
          setData("");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        setIsSubmitting(false);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
  };

  const handleRedirect = () => {
    const options = {
      key: process.env.REACT_APP_RAZOR_PAY_KEY,
      amount: orderDetails.amount,
      order_id: orderDetails.id,
      currency: orderDetails.currency,
      image: Logo,
      name: "Importerr",
      prefill: {
        email: "abcdef@gmailll.com",
        contact: "202-555-0183",
      },
      handler: function (response) {
        handleVerifyPayment(response.razorpay_payment_id);
      },
    };
    const paymentWindow = new window.Razorpay(options);
    paymentWindow.open();
  };

  const handleVerifyPayment = async (id) => {
    await WalletApiService.verifyOrder(id)
      .then(async (res) => {
        loader.showLoading(false);
        if (res.status.code === 200) {
          handleRefresh();
          message.showToast(res.status.message, ToastTypes.SUCCESS);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
      });
  };

useEffect(() => {
    if (orderDetails.amount) {
      handleRedirect();
    }
  }, [orderDetails]);

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Add money on your IM cash"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={!isSubmitting}
      shouldCloseOnEsc={!isSubmitting}
    >
      <div className="wallet-recharge-modal-container">
        <div className="wallet-recharge-modal-header">
          <h2 className="wallet-recharge-modal-title">
            <FaWallet className="wallet-recharge-modal-title-icon" />
            Add money on your IM cash
          </h2>
          <button
            type="button"
            onClick={closeModal}
            className="wallet-recharge-modal-close"
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleProceedToPay}>
          <div className="wallet-recharge-modal-body">
            <p className="wallet-recharge-modal-description">
              Enter the amount you want to add to your IM cash. Minimum recharge amount is ₹5,000. You will be redirected to a secure payment gateway to complete the transaction.
            </p>

            <div className="wallet-recharge-modal-form-group">
              <label htmlFor="wallet-amount" className="wallet-recharge-modal-label">
                Amount
                <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
              </label>
              <div className="wallet-recharge-modal-input-wrapper">
                <span className="wallet-recharge-modal-currency-symbol">₹</span>
                <input
                  id="wallet-amount"
                  type="number"
                  className={`wallet-recharge-modal-input has-currency ${error ? "error" : ""}`}
                  value={data}
                  onChange={handleOnchange}
                  placeholder="Ex-5000"
                  disabled={isSubmitting}
                  min="5000"
                  step="1"
                />
              </div>
              <div className="wallet-recharge-modal-error-container">
                {error ? (
                  <div className="wallet-recharge-modal-error">
                    <FaExclamationCircle className="wallet-recharge-modal-error-icon" />
                    <span>{error}</span>
                  </div>
                ) : (
                  <div className="wallet-recharge-modal-amount-hint">
                    Minimum amount: ₹5,000
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="wallet-recharge-modal-footer">
            <button
              type="button"
              onClick={closeModal}
              className="wallet-recharge-modal-btn wallet-recharge-modal-btn-cancel"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`wallet-recharge-modal-btn wallet-recharge-modal-btn-submit ${isSubmitting ? "loading" : ""}`}
              disabled={isSubmitting}
            >
              <span className="wallet-recharge-modal-btn-text">
                {!isSubmitting && <FaCreditCard className="wallet-recharge-modal-btn-icon" />}
                {isSubmitting ? "Processing..." : "Proceed to Pay"}
              </span>
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
